import * as THREE from 'three/webgpu';
import Locomotive from './Locomotive';
import { IS_DEBUG_ENABLED, loadGLTFModel, setToonMaterial } from './util';
import Machine from './Machine';

const FRICTION = 0.995;
const SPACE_BETWEEN_CARS_MAX = 0.15;
const SPACE_BETWEEN_CARS_MIN = 0;
const WEIGHT = 0.02;

type Tractor = Locomotive | Railcar;

export default class Railcar extends Machine {
    tractor: Tractor;
    locomotive: Locomotive;

    resistanceForce: number = 0;

    constructor(scene: THREE.Scene, tractor: Tractor, locomotive: Locomotive) {
        super(scene);

        this.tractor = tractor;
        this.locomotive = locomotive;
    }

    public async create() {
        if (!IS_DEBUG_ENABLED) {
            this.locomotive.attachRailcar(this);
            this.mesh = await loadGLTFModel('./railcar.glb');
            this.mesh.children.forEach((group: THREE.Object3D) => {
                group.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) {
                        const materials = Array.isArray(mesh.material)
                            ? mesh.material
                            : [mesh.material];

                        materials.forEach((material) => {
                            if (material.name === 'railcar') {
                                setToonMaterial(material, mesh);
                            }
                        });
                    }
                });
            });
        }

        if (IS_DEBUG_ENABLED) {
            this.mesh = new THREE.Mesh(
                new THREE.BoxGeometry(1, 1, 3),
                new THREE.MeshBasicMaterial({ color: 0x00ff00 })
            );
        }

        this.progress =
            this.tractor.progress -
            this.tractor.getLength() / 2 -
            this.getLength() / 2 -
            SPACE_BETWEEN_CARS_MIN;
        this.scene.add(this.mesh);
    }

    public animate() {
        const delta = this.tractor.progress - this.progress;
        const distance = Math.abs(delta);
        const direction = Math.sign(delta);

        const minDist =
            this.tractor.getLength() / 2 +
            this.getLength() / 2 +
            SPACE_BETWEEN_CARS_MIN;
        const maxDist =
            this.tractor.getLength() / 2 +
            this.getLength() / 2 +
            SPACE_BETWEEN_CARS_MAX;

        if (distance > maxDist) {
            this.progress = this.tractor.progress - direction * maxDist;
            this.speed = this.tractor.speed;
            this.resistanceForce = WEIGHT;
        } else if (distance < minDist) {
            this.progress = this.tractor.progress - direction * minDist;
            this.speed = this.tractor.speed;
            this.resistanceForce = WEIGHT;
        } else {
            this.speed *= FRICTION;
            if (Math.abs(this.speed) < 0.001) this.speed = 0;
            this.resistanceForce = 0;
        }

        this.progress += this.speed * 0.1;

        this.mesh.position.z = this.progress;
    }
}
